import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { getRequest } from '../../../../../services/base'
import { NavContext } from '../../../../../context/global_context'
import ItemLink from '../../../../common/ItemLink'
import { Flex, Form, List, Tabs, Tooltip, Typography } from 'antd'
import IconEndConnector from '../../../../icons/IconEndConnector'
import IconStartConnector from '../../../../icons/IconStartConnector'
import InputText from '../../../../common/InputText'
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import EllipsisTooltip from '../../../../common/EllipsisTooltip'

const MAPPING_TYPES = {
  START: 'start',
  END: 'end',
}

export const SearchConnector = ({ activeTab }) => {
  const intl = useIntl()
  const { vehicle } = useParams()

  const [searchValue, setSearchValue] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [connectors, setConnectors] = useState([])
  const [searchFailed, setSearchFailed] = useState(false)

  const [sortOrder, setSortOrder] = useState(null)
  const [form] = Form.useForm()
  const connectorsType = (activeTab === MAPPING_TYPES.START) ? (
    'Start'
  ) : (
    (activeTab === MAPPING_TYPES.END) ? ('End') : ('')
  )
  const inactiveTab = (activeTab === MAPPING_TYPES.START) ? (
    MAPPING_TYPES.END
  ) : (
    (activeTab === MAPPING_TYPES.END) ? (MAPPING_TYPES.START) : ('')
  )
  const searchLabel = intl.formatMessage({ id: 'dtcs.connectorMapping.searchConnectors' }, { type: connectorsType })
  const searchInst = intl.formatMessage({ id: 'dtcs.connectorMapping.searchInstruction' }, { type: connectorsType })

  useEffect(() => {
    if (searchParams.has(`${activeTab}_q`)) {
      getConnectors(searchParams.get(`${activeTab}_q`))
    }
  }, [])

  const getConnectors = async (searchValue) => {
    let res = await getRequest(
      `/api/connector/?search=${searchValue}&vehicle=${vehicle}`
    )
    // Driven by the search box, so the failure replaces the placeholder below
    // rather than raising a toast on every search.
    if (res?.status === false) {
      setSearchFailed(true)
      setConnectors([])
      return
    }
    setSearchFailed(false)
    setConnectors(res.results)
  }

  // Sort Connectors
  const sortedData = useMemo(() => {
    if (!sortOrder) return connectors
    
    return [...connectors].sort((a, b) => {
      return sortOrder === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    })
  }, [connectors, sortOrder])

  const toggleSort = () => {
    setSortOrder((prev) => {
      switch (prev) {
        case null:
          return 'asc'
        case 'asc':
          return 'desc'
        default:
          return null
      }
    })
  }

  const ListHeader = () => {
    return (
      <div className="importer-grid">
        <Flex
          align="center"
          gap={8}
          className="sorting-column-header"
          onClick={toggleSort}
        >
          <Typography.Text>{intl.formatMessage({ id: 'dtcs.connectorMapping.selectConnector' })}</Typography.Text>
          <Flex vertical gap={0} className="sort-icon">
            <CaretUpOutlined className={sortOrder === 'asc' ? 'active' : ''} />
            <CaretDownOutlined
              className={sortOrder === 'desc' ? 'active' : ''}
            />
          </Flex>
        </Flex>
        <div></div>
      </div>
    )
  }

  useEffect(() => {
    if (!form) return

    form.setFieldsValue({
      search_connector: searchParams.getAll(`${activeTab}_q`) ?? '',
    })
  }, [form, searchParams, activeTab])

  return (
    <div className="col-body">
      <Form
        /* id={`search-${activeTab}-connectors-form`}
        name={`search-${activeTab}-connectors-form`} */
        form={form}
        layout="vertical"
      >
        <InputText
          type="search"
          name="search_connector"
          label={searchLabel}
          placeholder={searchLabel}
          // value={searchValue}
          hideLabel={true}
          isRequired={false}
          wrapperClass="justify-items-evenly"
          onChange={(value) => {
            setSearchValue(value)
            if (!value) {
              setSearchParams({
                dtc_id: searchParams.getAll('dtc_id'),
                dtc_mapping_type: searchParams.get('dtc_mapping_type'),
                ...(searchParams.has(`${inactiveTab}_q`)
                  ? {
                      [`${inactiveTab}_q`]: searchParams.get(
                        `${inactiveTab}_q`
                      ),
                    }
                  : {}),
              })
              setConnectors([])
              setSearchFailed(false)
            }
          }}
          onSubmit={(value) => {
            setSearchValue(value)
            if (!value) {
              setSearchParams({
                dtc_id: searchParams.getAll('dtc_id'),
                dtc_mapping_type: searchParams.get('dtc_mapping_type'),
                ...(searchParams.has(`${inactiveTab}_q`)
                  ? {
                      [`${inactiveTab}_q`]: searchParams.get(
                        `${inactiveTab}_q`
                      ),
                    }
                  : {}),
              })
              setConnectors([])
            } else {
              getConnectors(value ? value : '')
              setSearchParams({
                dtc_id: searchParams.getAll('dtc_id'),
                dtc_mapping_type: searchParams.get('dtc_mapping_type'),
                [`${activeTab}_q`]: value ? value : '',
                ...(searchParams.has(`${inactiveTab}_q`)
                  ? {
                      [`${inactiveTab}_q`]: searchParams.get(
                        `${inactiveTab}_q`
                      ),
                    }
                  : {}),
              })
            }
          }}
        />
      </Form>

      {connectors?.length > 0 ? (
        <>
          <List
            bordered
            dataSource={sortedData}
            className="connectors-list"
            header={<ListHeader />}
            renderItem={(connector, index) => (
              <List.Item data-testid="dtc-connector-result" data-name={connector.name}>
                <ItemLink
                  name={connector.name}
                  requestUri={`/vehicles/${vehicle}/dtcs/listing/mappings/${
                    connector.id
                  }/?${createSearchParams({
                    dtc_id: searchParams.getAll('dtc_id'),
                    connector_name: connector.name,
                    dtc_mapping_type: activeTab,
                    [`${activeTab}_q`]:
                      searchParams.getAll(`${activeTab}_q`) ?? '',
                    [`${inactiveTab}_q`]:
                      searchParams.getAll(`${inactiveTab}_q`) ?? '',
                  })}`}
                  description={connector.description}
                  infoLines={[]}
                  key={`connector-item-${index}`}
                />
              </List.Item>
            )}
          />
        </>
      ) : (
        <Flex
          justify="center"
          align="center"
          vertical
          className="no-connectors-container"
        >
          <Typography.Text type={searchFailed ? 'danger' : undefined}>
            {searchFailed
              ? intl.formatMessage({ id: 'generic.searchFailed' })
              : searchInst}
          </Typography.Text>
        </Flex>
      )}
    </div>
  )
}

const SearchConnectorSection = () => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [navState, navDispatch] = useContext(NavContext)

  const [dtcMappingType, setDtcMappingType] = useState(null)

  const tabItems = useMemo(() => [
    {
      key: MAPPING_TYPES.START,
      icon: <IconStartConnector />,
      label: (
        <EllipsisTooltip className="tab-label">
          {intl.formatMessage({ id: 'dtcs.connectorMapping.startConnector' })}
        </EllipsisTooltip>
      ),
      children: <SearchConnector activeTab={dtcMappingType} />,
    },
    {
      key: MAPPING_TYPES.END,
      icon: <IconEndConnector />,
      label: (
        <EllipsisTooltip className="tab-label">{intl.formatMessage({ id: 'dtcs.connectorMapping.endConnector' })}</EllipsisTooltip>
      ),
      children: <SearchConnector activeTab={dtcMappingType} />,
    },
  ], [dtcMappingType])

  useEffect(() => {
    if(!dtcMappingType) return

    const params = new URLSearchParams(searchParams)
    params.set('dtc_mapping_type', dtcMappingType)
    setSearchParams(params)
    // console.log('Updating SEARCH PARAMS: ', dtcMappingType)
  }, [dtcMappingType])

  useEffect(() => {
    let value = (navState.destination_only) ? (
      'start'
    ) : (
      searchParams.get('dtc_mapping_type') ?? 'end'
    )
    // console.log('Type: ', value)
    setDtcMappingType(value)
  }, [])

  return (
    <>
      {!navState.destination_only ? (
        <Flex className="col">
          <Tabs
            items={tabItems}
            activeKey={dtcMappingType}
            centered={true}
            tabBarGutter={0}
            className="connector-tabs"
            animated={false}
            // destroyOnHidden={true}
            onChange={(value) => {
              setDtcMappingType(value)
            }}
          />
        </Flex>
      ) : (
        <Flex vertical gap={16} className="col">
          <Typography.Title level={5} className="col-header">
            <Typography.Text className="icon non-interactive">
              <IconStartConnector />
            </Typography.Text>
            <Typography.Text className="title non-interactive">
              {intl.formatMessage({ id: 'dtcs.connectorMapping.startConnector' })}
            </Typography.Text>
          </Typography.Title>

          <SearchConnector activeTab={dtcMappingType} />
        </Flex>
      )}
    </>
  )
}

export default SearchConnectorSection
